/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import { BuilderFormSchemaFactory, BuilderUserContext, FormRootSchema, FormStateSchema, isJobEditable, JobDetailsModel } from '@gh/builder-shared';
import { BuilderPermission } from '@gh/builder-shared-data';
import { DateTimeTypes, FormState, NumberTypes, toAppFormControl, whenEditMode } from '@gh/common-shared';
import { FormMode, getControlValue } from '@gh/core-ui';
import { currentDate } from '@gh/core-util';

import { isNil } from 'lodash';
import { getDisplayedJobInsurance, JobInsurance } from '../../../resources/job-details-tab/job-details-tab-model';

@Injectable()
export class JobDetailsInsuranceTabFormState extends FormState<JobInsurance> {
  private originalInsurance: JobInsurance;

  constructor(schemaFactory: BuilderFormSchemaFactory,
              private userContext: BuilderUserContext,
              private model: JobDetailsModel) {
    super(schemaFactory);
  }

  build(insurance: JobInsurance): void {
    this.originalInsurance = insurance;

    this.initSchema(getDisplayedJobInsurance(insurance, insurance));
  }

  merge(insurance: JobInsurance): void {
    this.group.patchValue(insurance);
  }

  onPolicyNumberChange(): void {
    this.updatePolicyData();
    this.updateStartDate();
  }

  protected buildSchema(insurance: JobInsurance, { root }: FormStateSchema): FormRootSchema {
    const mode =  this.userContext.hasPermission(BuilderPermission.JobAdmin.JobDetails.EDIT)
    && isJobEditable(this.model.basicInfo)
      ? FormMode.EditMode
      : FormMode.ViewMode;

    return root({
      mode,
      enabled: whenEditMode(),
      optimisticLock: true,
      children: {
        id: void 0,
        insurancePolicyNumber: void 0,
        insuranceStartDate: { enabled: !isNil(insurance.insurancePolicyNumber), type: DateTimeTypes.ShortDate },
        insuranceCompanyId: void 0,
        insuranceCompanyName: { enabled: false },
        insuranceAmount: { enabled: false, type: NumberTypes.Price },
        version: void 0,
      },
    });
  }

  private updatePolicyData(): void {
    this.merge(getDisplayedJobInsurance(this.group.getRawValue(), this.originalInsurance));
  }

  private updateStartDate(): void {
    const control = this.getControl('insuranceStartDate');
    const policyNumberValue = getControlValue(this.group, 'insurancePolicyNumber');
    const isPolicyNumberEmpty = isNil(policyNumberValue) || policyNumberValue === '';

    toAppFormControl(control).updateSchema({ enabled: !isPolicyNumberEmpty });
    if (!isPolicyNumberEmpty) {
      control.setValue(currentDate());
    }
  }
}
